// 블록 ⑤ 매출액 추이 — 월별 막대 + 누적 라인 (mock 전용, 게이트A-2).
//
// 현정 범위는 차트 UI + mock. 실데이터(SF Amount 귀속·스테이지)는 혜원 Financial 이관.
// 인라인 SVG(목업 그대로, portal 기존 App.tsx 패턴). 색은 blue 스케일 토큰만.
// revenue 단위 = 억 원(계약). 빈 배열이면 empty state(SF 미연결 대비, graceful).
import { useState, type CSSProperties } from 'react'
import type { RevenuePoint } from '../../creatorsApi'
import { xBand, yAt, type ChartGeom } from '../chart'
import { formatMonthShort } from './format'

const W = 480
const H = 160
const PAD_X = 24
const TOP = 24
const BOTTOM = 140
const GEOM: ChartGeom = { w: W, h: H, padX: PAD_X, top: TOP, bottom: BOTTOM }

const won = (v: number) => `₩${v.toFixed(1)}억`

/** Overview 매출 블록의 표시 창 — 목업(Creators.dc.html) KPI 첫 칸이 "6개월 합계"다.
 *  BE 는 전 기간을 내려주므로(Financial 전량 재집계) 자르는 건 화면 몫이다. */
const WINDOW_MONTHS = 6

export function RevenueTrendChart({ points: all }: { points: RevenuePoint[] }) {
  const [hover, setHover] = useState<number | null>(null)
  // 최근 6개월만 그린다. 전 기간을 그리면 월이 수십 개라 막대가 실처럼 얇아지고 라벨이 뭉갠다.
  const points = all.slice(-WINDOW_MONTHS)
  // 창 **첫 달**의 전월 대비는 창 밖(직전 달) 값이 있어야 낼 수 있다. 없으면 그 달만 "—".
  const prevOfFirst = all.length > points.length ? all[all.length - points.length - 1].revenue : null
  const n = points.length

  return (
    <div className="min-w-0 border border-line-hairline bg-paper">
      {n === 0 ? (
        <div className="p-[15px_20px]">
          <SectionTitle />
          <div className="mt-4 border border-dashed border-line-soft p-8 text-center font-text text-[14px] text-fg-3">
            매출 데이터가 아직 없습니다.
          </div>
        </div>
      ) : (
        <RevenueBody points={points} prevOfFirst={prevOfFirst} hover={hover} setHover={setHover} />
      )}
    </div>
  )
}

function SectionTitle() {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block h-[5px] w-[5px] bg-brand-blue" aria-hidden />
      <h3 className="font-display text-[20px] font-bold tracking-[-0.01em] text-fg-1">매출액 추이 · 월별</h3>
    </div>
  )
}

function RevenueBody({
  points,
  prevOfFirst,
  hover,
  setHover,
}: {
  points: RevenuePoint[]
  /** 창 바로 앞 달의 매출. 창 첫 달의 전월 대비 계산에만 쓴다. null 이면 그 달은 "—". */
  prevOfFirst: number | null
  hover: number | null
  setHover: (i: number | null) => void
}) {
  const n = points.length
  const revenues = points.map((p) => p.revenue)
  // 합계·누적은 **창 기준**으로 다시 낸다. BE 의 cumulative 는 전 기간 누적이라
  // 그대로 쓰면 "6개월 합계"가 6개월의 합이 아니게 되고, 누적선도 창 밖에서 이미
  // 높이 올라온 상태로 시작해 막대를 눌러버린다.
  const total = revenues.reduce((a, b) => a + b, 0)
  const avg = total / n
  const cums = revenues.reduce<number[]>((acc, r) => [...acc, (acc[acc.length - 1] ?? 0) + r], [])
  const peak = points.reduce((a, b) => (b.revenue > a.revenue ? b : a))
  // 전월 대비 = 창 마지막 달 vs 그 직전 달. 창 안에 직전 달이 없으면(첫 달뿐) 창 밖 값을 쓴다.
  const prevOfLast = n >= 2 ? points[n - 2].revenue : prevOfFirst
  const momLast = prevOfLast != null ? mom(points[n - 1].revenue, prevOfLast) : null

  const step = (W - PAD_X * 2) / n
  const barW = Math.min(46, step * 0.62)
  const cx = (i: number) => xBand(i, n, GEOM)
  const revMax = Math.max(...revenues, 0.0001) * 1.1
  const cumMax = Math.max(...cums, 0.0001)
  const barY = (rev: number) => yAt(rev, revMax, GEOM)
  const barH = (rev: number) => BOTTOM - barY(rev)
  const cumY = (cum: number) => yAt(cum, cumMax, GEOM)

  const cumPts = cums.map((c, i) => `${cx(i).toFixed(1)},${cumY(c).toFixed(1)}`)
  const cumArea = `${cx(0).toFixed(1)},${BOTTOM} ${cumPts.join(' ')} ${cx(n - 1).toFixed(1)},${BOTTOM}`

  const kpis: { label: string; value: string; tone?: 'up' | 'down' }[] = [
    { label: `${n}개월 합계`, value: won(total) },
    { label: '월평균 매출', value: won(avg) },
    { label: '최고 매출 월', value: `${formatMonthShort(peak.month)} ${won(peak.revenue)}` },
    momLast
      ? { label: '전월 대비', value: momLast.text, tone: momLast.up ? 'up' : 'down' }
      : { label: '전월 대비', value: '—' },
  ]

  // 라벨은 **막대 중앙**에 둔다. 양끝을 left:0 / right:0 으로 고정하던 규칙은 월이 수십 개일 때
  // 클립을 막으려던 것인데, 6개 구간에선 첫·끝 라벨만 가장자리로 밀려 "붙어 보이는" 원인이 된다.
  // 막대 밴드(xBand)가 이미 PAD_X 만큼 안쪽에서 시작하므로 중앙 정렬로도 넘치지 않는다.
  const labelStyle = (i: number): CSSProperties => ({
    left: `${(cx(i) / W) * 100}%`,
    transform: 'translateX(-50%)',
  })

  return (
    <>
      {/* KPI 4셀 */}
      <div className="grid grid-cols-2 gap-px border-b border-line-hairline bg-line-hairline">
        {kpis.map((k, i) => (
          <div key={k.label} className={i === 0 ? 'bg-surface-soft p-[16px_20px]' : 'bg-surface-base p-[16px_20px]'}>
            <div className="font-text text-[11px] text-fg-3">{k.label}</div>
            <div
              className={[
                'mt-1 whitespace-nowrap font-display font-bold tabular-nums',
                k.label === '최고 매출 월' ? 'text-[18px]' : 'text-[24px]',
                k.tone === 'up' ? 'text-status-success' : k.tone === 'down' ? 'text-status-danger' : 'text-fg-1',
              ].join(' ')}
            >
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* 차트 */}
      <div className="p-[16px_20px]">
        <div className="mb-2 flex items-center justify-between">
          <SectionTitle />
          <div className="flex gap-4 font-text text-[11px] text-fg-3">
            <span className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 bg-brand-blue" aria-hidden />월 매출
            </span>
            <span className="flex items-center gap-2">
              <span className="inline-block h-0.5 w-2.5 bg-blue-300" aria-hidden />
              누적
            </span>
          </div>
        </div>

        <div className="relative">
          <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full">
            <defs>
              <linearGradient id="revbar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--brand-blue)" />
                <stop offset="100%" stopColor="var(--blue-200)" />
              </linearGradient>
            </defs>
            {/* 그리드 */}
            <line x1={PAD_X} y1={TOP} x2={W - PAD_X} y2={TOP} stroke="var(--border-hairline)" />
            <line x1={PAD_X} y1={(TOP + BOTTOM) / 2} x2={W - PAD_X} y2={(TOP + BOTTOM) / 2} stroke="var(--border-hairline)" />
            <line x1={PAD_X} y1={BOTTOM} x2={W - PAD_X} y2={BOTTOM} stroke="var(--border-soft)" />
            {/* 누적 영역 */}
            <polygon points={cumArea} fill="color-mix(in srgb, var(--brand-blue) 7%, transparent)" />
            {/* 막대 */}
            {points.map((p, i) => (
              <rect
                key={p.month}
                x={cx(i) - barW / 2}
                y={barY(p.revenue)}
                width={barW}
                height={barH(p.revenue)}
                fill={hover === i ? 'var(--brand-blue)' : 'url(#revbar)'}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                className="cursor-pointer"
              />
            ))}
            {/* 누적 라인 + 포인트 */}
            <polyline
              points={cumPts.join(' ')}
              fill="none"
              stroke="var(--blue-300)"
              strokeWidth={2}
              strokeDasharray="4 3"
              strokeLinejoin="round"
            />
            {points.map((p, i) => (
              <circle
                key={p.month}
                cx={cx(i)}
                cy={cumY(cums[i])}
                r={i === n - 1 ? 3.5 : 2.5}
                fill={i === n - 1 ? 'var(--brand-blue)' : 'var(--blue-300)'}
              />
            ))}
          </svg>

          {/* 호버 툴팁 */}
          {hover !== null && (
            <div
              className="pointer-events-none absolute -translate-x-1/2 -translate-y-[135%] whitespace-nowrap bg-brand-blue px-2 py-[3px] font-display text-[12px] font-bold text-white shadow-lf-2"
              style={{ left: `${(cx(hover) / W) * 100}%`, top: `${(barY(points[hover].revenue) / H) * 100}%` }}
            >
              {formatMonthShort(points[hover].month)} · {won(points[hover].revenue)}
              {hover > 0 ? ` · ${mom(points[hover].revenue, points[hover - 1].revenue).text}` : ''}
            </div>
          )}
        </div>

        {/* x축 라벨 — 창이 6개월이라 **매월** 찍는다(목업과 동일). pickAxisLabels 는 수십 개월을
            연도 경계로 솎는 장치라 6개 구간에선 라벨이 1~2개만 남아 양끝에 붙어 보였다. */}
        <div className="relative mt-2 h-[15px]">
          {points.map((p, i) => (
            <span
              key={p.month}
              className="absolute top-0 whitespace-nowrap font-text text-[11px] text-fg-3"
              style={labelStyle(i)}
            >
              {formatMonthShort(p.month)}
            </span>
          ))}
        </div>
      </div>
    </>
  )
}

function mom(cur: number, prev: number): { text: string; up: boolean } {
  if (!Number.isFinite(prev) || prev === 0) return { text: '—', up: true }
  const g = ((cur - prev) / prev) * 100
  return { text: `${g >= 0 ? '+' : ''}${g.toFixed(0)}%`, up: g >= 0 }
}
